import React from 'react';
import {useForm} from 'react-hook-form';
import {commentCase, commentToolbox, commentToolboxTool} from '../../features/thunks';
import {useDispatch, useSelector} from 'react-redux';
import {useAuth} from '../../util/auth';
import {selectCaseById} from '../../features/casesSlice';
import {selectToolboxById} from '../../features/toolboxSlice';
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import {Form, FormControl, FormField, FormItem, FormMessage} from '@/components/ui/form';
import {Button} from '@/components/ui/button';
import {Textarea} from '@/components/ui/textarea';

const formSchema = z.object({
    comment: z.string().min(3, {
        message: "Comment must be at least 3 characters.",
    }),
})

export const CaseAddReactionForm = ({
    caseId,
    onSubmitCommentButtonClick,
    afterCommentSubmitted,
    ctaText = 'Add comment',
    placeholder = 'What are your thoughts?',
}: {
    caseId: string;
    onSubmitCommentButtonClick?: () => void;
    afterCommentSubmitted?: () => void;
    ctaText?: string;
    placeholder?: string;
}) => {
    const currentCase = useSelector((state) => selectCaseById(state, caseId));
    const dispatch = useDispatch();
    const auth = useAuth();

    if (!currentCase) {
        return null;
    }

    return <AddReactionForm
        ctaText={ctaText}
        placeholder={placeholder}
        onSubmitCommentButtonClick={(comment) => {
            if (!auth?.user) {
                return null;
            }
            dispatch(commentCase({caseId, comment, userId: auth.user.id})).then(() => {
                if (afterCommentSubmitted && typeof afterCommentSubmitted === 'function') {
                    afterCommentSubmitted()
                }
            });
            if (onSubmitCommentButtonClick && typeof onSubmitCommentButtonClick === 'function') {
                onSubmitCommentButtonClick()
            }
        }}
    />
}

type AddReactionFormProps = {
    toolboxId?: string;
    toolId?: string;
    onSubmitCommentButtonClick?: (comment: string) => void;
    afterCommentSubmitted?: () => void;
    placeholder?: string;
    ctaText?: string;
}
export const ToolboxAddReactionForm = ({
    toolboxId,
    toolId,
    placeholder,
    onSubmitCommentButtonClick,
    afterCommentSubmitted,
    ctaText
}: AddReactionFormProps) => {
    const currentToolbox = useSelector(state => selectToolboxById(state, toolboxId || ''));
    const dispatch = useDispatch();
    const auth = useAuth();

    if (!currentToolbox) {
        return null;
    }

    return <AddReactionForm
        placeholder={placeholder}
        ctaText={ctaText}
        onSubmitCommentButtonClick={(comment) => {
            if (!auth?.user) {
                return;
            }
            if (toolId) {
                dispatch(commentToolboxTool({toolId, comment, userId: auth.user.id})).then(() => {
                    if (afterCommentSubmitted && typeof afterCommentSubmitted === 'function') {
                        afterCommentSubmitted()
                    }
                })
            } else if (toolboxId) {
                dispatch(commentToolbox({toolboxId, comment, userId: auth.user.id})).then(() => {
                    if (afterCommentSubmitted && typeof afterCommentSubmitted === 'function') {
                        afterCommentSubmitted()
                    }
                })
            }

            if (onSubmitCommentButtonClick && typeof onSubmitCommentButtonClick === 'function') {
                onSubmitCommentButtonClick(comment)
            }
        }}
    />
}

const AddReactionForm = ({
    onSubmitCommentButtonClick,
    placeholder = 'What are your thoughts?',
    ctaText = 'Add comment'
}: {
    onSubmitCommentButtonClick: (comment: string) => void;
    placeholder?: string;
    ctaText?: string;
}) => {
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            comment: "",
        },
    })

    function onSubmit(values: z.infer<typeof formSchema>) {
        if (typeof onSubmitCommentButtonClick === 'function') {
            onSubmitCommentButtonClick(values.comment);
            form.reset();
        }
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                    control={form.control}
                    name="comment"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Textarea placeholder={placeholder} {...field} className="bg-backgroundBrand"/>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <Button type="submit">{ctaText}</Button>
            </form>
        </Form>
    )
}

